import { createRouter, createWebHistory } from 'vue-router'
import { Role } from '@smm/types'
import { useAuthStore } from '@/stores/auth.js'
import { useSettingsStore } from '@/stores/settings.js'
import { LOCALE_SEGMENT_PATTERN, localeBySegment, localeByCode, localisePath } from '@/i18n/locales.js'
import { i18n, readStoredLocale, setLocale } from '@/i18n/index.js'
import { applySeo } from '@/lib/seo.js'

/**
 * Routing and access control.
 *
 * Every view is lazily imported, so the admin bundle is never downloaded by a
 * customer who cannot reach it.
 *
 * Guards here are a convenience, not a security boundary. They decide what to
 * *render*; the API decides what a request may *do*. Anyone can edit the
 * client, so a route guard that is not mirrored by a server check protects
 * nothing — which is why every admin route has a matching `requireRole` on the
 * API side.
 */

/**
 * Every public path may carry a language in front of it: `/tl/services`,
 * `/th/help`. English carries no prefix, so `/services` is English and stays
 * the canonical URL. The segment is a real route parameter rather than a
 * query, so a translated page can be linked, shared and indexed.
 */
const L = `:locale(${LOCALE_SEGMENT_PATTERN})?`

const routes = [
  /* ─────────────────────────────────────────────────────────────  public ── */
  {
    path: `/${L}`,
    component: () => import('@/layouts/PublicLayout.vue'),
    children: [
      { path: '', name: 'home', component: () => import('@/views/public/HomeView.vue') },
      {
        path: 'services',
        name: 'public-services',
        component: () => import('@/views/public/ServicesView.vue'),
      },
      // Services and pricing were two pages showing the same catalogue. They
      // are one page now; the old URL is kept because it is linked and may be
      // bookmarked.
      { path: 'pricing', redirect: '/services' },
      { path: 'faq', name: 'faq', component: () => import('@/views/public/FaqView.vue') },
      { path: 'help', name: 'help', component: () => import('@/views/public/HelpView.vue') },
      {
        path: 'help/ticket/:number(\\d+)',
        name: 'help-ticket',
        component: () => import('@/views/public/ContactTicketView.vue'),
      },
      // The help centre absorbed the contact page. Old links keep working.
      // Where a guest lands after paying: the payment's state, then the orders.
      {
        path: 'order/status/:reference',
        name: 'guest-order-status',
        component: () => import('@/views/public/GuestOrderStatusView.vue'),
      },
      { path: 'contact', redirect: '/help' },
      { path: 'contact/ticket/:number(\\d+)', redirect: (to) => `/help/ticket/${to.params.number}` },
      // One return URL for every hosted checkout; it resolves the invoice and moves on.
      { path: 'pay/return', name: 'pay-return', component: () => import('@/views/public/PayReturnView.vue') },
      // The legal documents in the reader's language; English is untouched and
      // is what any language without a translation reads.
      {
        path: 'terms',
        name: 'terms',
        component: () => import('@/views/public/legal/LegalPage.vue'),
        props: { doc: 'terms' },
      },
      {
        path: 'privacy',
        name: 'privacy',
        component: () => import('@/views/public/legal/LegalPage.vue'),
        props: { doc: 'privacy' },
      },
    ],
  },

  /* ────────────────────────────────────────────────────────────────  auth ── */
  {
    path: `/${L}`,
    component: () => import('@/layouts/AuthLayout.vue'),
    // Signed-in users have no business on a login page; send them to the app.
    meta: { guestOnly: true },
    children: [
      { path: 'login', name: 'login', component: () => import('@/views/auth/LoginView.vue') },
      { path: 'register', name: 'register', component: () => import('@/views/auth/RegisterView.vue') },
      {
        path: 'forgot-password',
        name: 'forgot-password',
        component: () => import('@/views/auth/ForgotPasswordView.vue'),
      },
      {
        path: 'reset-password',
        name: 'reset-password',
        component: () => import('@/views/auth/ResetPasswordView.vue'),
      },
      // The link in a guest order email: choose a username and password, and
      // the paid orders are yours.
      {
        path: 'claim/:token',
        name: 'claim-account',
        component: () => import('@/views/auth/ClaimAccountView.vue'),
      },
    ],
  },
  {
    // Not `guestOnly`: a user often verifies while already signed in.
    path: '/verify-email',
    name: 'verify-email',
    component: () => import('@/views/auth/VerifyEmailView.vue'),
    meta: { layout: 'auth' },
  },

  /* ──────────────────────────────────────────────────────── customer app ── */
  {
    path: '/app',
    component: () => import('@/layouts/AppLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      { path: '', name: 'dashboard', component: () => import('@/views/app/DashboardView.vue') },
      { path: 'order', name: 'new-order', component: () => import('@/views/app/NewOrderView.vue') },
      { path: 'orders', name: 'orders', component: () => import('@/views/app/OrdersView.vue') },
      {
        path: 'orders/:id',
        name: 'order-detail',
        component: () => import('@/views/app/OrderDetailView.vue'),
      },
      { path: 'services', name: 'services', component: () => import('@/views/app/ServicesView.vue') },
      { path: 'wallet', name: 'wallet', component: () => import('@/views/app/WalletView.vue') },
      { path: 'wallet/deposit', name: 'deposit', component: () => import('@/views/app/DepositView.vue') },
      { path: 'payments', name: 'payments', component: () => import('@/views/app/PaymentsView.vue') },
      { path: 'support', name: 'support', component: () => import('@/views/app/SupportView.vue') },
      { path: 'support/:id', name: 'ticket', component: () => import('@/views/app/TicketView.vue') },
      { path: 'profile', name: 'profile', component: () => import('@/views/app/ProfileView.vue') },
      { path: 'settings', name: 'settings', component: () => import('@/views/app/SettingsView.vue') },
    ],
  },

  /* ────────────────────────────────────────────────────────────── admin ── */
  {
    path: '/admin',
    component: () => import('@/layouts/AdminLayout.vue'),
    meta: { requiresAuth: true, minRole: Role.SUPPORT },
    children: [
      { path: '', name: 'admin-dashboard', component: () => import('@/views/admin/AdminDashboardView.vue') },
      { path: 'users', name: 'admin-users', component: () => import('@/views/admin/AdminUsersView.vue') },
      {
        path: 'users/:id',
        name: 'admin-user',
        component: () => import('@/views/admin/AdminUserDetailView.vue'),
      },
      { path: 'orders', name: 'admin-orders', component: () => import('@/views/admin/AdminOrdersView.vue') },
      {
        path: 'orders/:id',
        name: 'admin-order',
        component: () => import('@/views/admin/AdminOrderDetailView.vue'),
      },
      {
        path: 'services',
        name: 'admin-services',
        component: () => import('@/views/admin/AdminServicesView.vue'),
        meta: { minRole: Role.ADMIN },
      },
      {
        path: 'categories',
        name: 'admin-categories',
        component: () => import('@/views/admin/AdminCategoriesView.vue'),
        meta: { minRole: Role.ADMIN },
      },
      {
        path: 'providers',
        name: 'admin-providers',
        component: () => import('@/views/admin/AdminProvidersView.vue'),
        meta: { minRole: Role.ADMIN },
      },
      {
        path: 'pricing',
        name: 'admin-pricing',
        component: () => import('@/views/admin/AdminPricingView.vue'),
        meta: { minRole: Role.ADMIN },
      },
      {
        path: 'routing',
        name: 'admin-routing',
        component: () => import('@/views/admin/AdminRoutingView.vue'),
        meta: { minRole: Role.ADMIN },
      },
      {
        path: 'providers/:id',
        name: 'admin-provider',
        component: () => import('@/views/admin/AdminProviderDetailView.vue'),
        meta: { minRole: Role.ADMIN },
      },
      {
        path: 'payments',
        name: 'admin-payments',
        component: () => import('@/views/admin/AdminPaymentsView.vue'),
      },
      {
        path: 'wallet',
        name: 'admin-wallet',
        component: () => import('@/views/admin/AdminWalletView.vue'),
        meta: { minRole: Role.ADMIN },
      },
      {
        path: 'tickets',
        name: 'admin-tickets',
        component: () => import('@/views/admin/AdminTicketsView.vue'),
      },
      {
        path: 'tickets/:id',
        name: 'admin-ticket',
        component: () => import('@/views/admin/AdminTicketDetailView.vue'),
      },
      {
        path: 'audit',
        name: 'admin-audit',
        component: () => import('@/views/admin/AdminAuditView.vue'),
        meta: { minRole: Role.ADMIN },
      },
      {
        path: 'settings',
        name: 'admin-settings',
        component: () => import('@/views/admin/AdminSettingsView.vue'),
        meta: { minRole: Role.ADMIN },
      },
    ],
  },

  /* ───────────────────────────────────────────────────────────── fallback ── */
  /**
   * Checkpoint 3 prototype set. Standalone rather than inside a layout, so the
   * new language can be judged without the old chrome around it. Not linked
   * from any navigation; remove this route once the sweep is done.
   */

  // English is the unprefixed site, so `/en/...` is never a real page. Links
  // like that were issued (a guest payment return built `/en/order/status/…`)
  // and may still be in inboxes and browser history; send them home.
  {
    path: '/en/:rest(.*)*',
    redirect: (to) => ({
      path: `/${[].concat(to.params.rest ?? []).join('/')}`,
      query: to.query,
      hash: to.hash,
    }),
  },
  { path: '/en', redirect: (to) => ({ path: '/', query: to.query, hash: to.hash }) },

  { path: '/:pathMatch(.*)*', name: 'not-found', component: () => import('@/views/NotFoundView.vue') },
]

export const router = createRouter({
  history: createWebHistory(),
  routes,

  scrollBehavior(to, from, savedPosition) {
    // Back/forward should land where the user left off.
    if (savedPosition) return savedPosition
    if (to.hash) return { el: to.hash, behavior: 'smooth' }
    // Changing only the query string is a filter change, not a new page —
    // scrolling to the top there would throw away the user's place in a list.
    if (to.path === from.path) return false
    return { top: 0 }
  },
})

/** Walks the matched route chain so a child's `minRole` overrides its parent's. */
function resolveMeta(route, key) {
  for (let i = route.matched.length - 1; i >= 0; i--) {
    const value = route.matched[i].meta?.[key]
    if (value !== undefined) return value
  }
  return undefined
}

/**
 * Keeps the language, the URL and the stored preference in step.
 *
 * A prefix in the URL wins: it is what was linked or shared. Without one, a
 * language the visitor chose earlier redirects to its own prefix, so their
 * choice survives an internal link written as `/services`. The panel and the
 * admin area are staff-facing and stay in English.
 */
router.beforeEach((to) => {
  const segment = to.params.locale
  const isTranslatable = !to.path.startsWith('/app') && !to.path.startsWith('/admin')

  if (segment) {
    setLocale(localeBySegment(segment).code, { remember: true })
    return true
  }

  if (isTranslatable) {
    const stored = localeByCode(readStoredLocale())
    if (stored.segment) {
      return { path: localisePath(stored.code, to.path), query: to.query, hash: to.hash }
    }
  }

  setLocale('en', { remember: false })
  return true
})

router.beforeEach(async (to) => {
  const auth = useAuthStore()
  const settings = useSettingsStore()

  // The first navigation has to wait for the session restore, or a signed-in
  // user landing on /app would be bounced to the login page.
  if (auth.isInitializing) await auth.initialize()
  if (!settings.isLoaded) await settings.load()

  const requiresAuth = resolveMeta(to, 'requiresAuth')
  const guestOnly = resolveMeta(to, 'guestOnly')
  const minRole = resolveMeta(to, 'minRole')

  if (requiresAuth && !auth.isAuthenticated) {
    // Remember where they were going, so the login can send them back.
    return { name: 'login', query: { redirect: to.fullPath } }
  }

  if (guestOnly && auth.isAuthenticated) {
    return { name: auth.isStaff ? 'admin-dashboard' : 'dashboard' }
  }

  if (minRole && !auth.hasRole(minRole)) {
    return { name: auth.isAuthenticated ? 'dashboard' : 'login' }
  }

  return true
})

/** Keeps the tab title in step with the current view. */
router.afterEach((to) => {
  const settings = useSettingsStore()
  const titles = {
    home: null,
    login: 'Sign in',
    register: 'Create account',
    dashboard: 'Now',
    'new-order': 'New order',
    orders: 'Orders',
    services: 'Services',
    wallet: 'Wallet',
    deposit: 'Add funds',
    payments: 'Payments',
    support: 'Support',
    profile: 'Profile',
    settings: 'Settings',
    'admin-dashboard': 'Admin',
  }

  // Public pages: canonical, hreflang alternates, description and a title in
  // the reader's language. Everything else: noindex.
  const seoTitle = applySeo(to, i18n.global.locale.value, settings.siteName)
  const page = seoTitle ?? titles[to.name]
  document.title = page ? `${page} · ${settings.siteName}` : settings.siteName
})
